// In-memory cache for group reference data (events, slot settings, site settings,
// workflow states, payment methods, saved filters, profile, unread count).
//
// Why: every console page refetched the same reference data on mount, and for a
// venue far from the API each round trip is a quarter second. Pages now read
// through getCached so a second page in the same session reuses the first page's
// result, and concurrent callers share one in-flight request.
//
// Scope and safety:
// - Entries are keyed by the current group id. A group switch always reloads the
//   page, so memory never carries across groups; the key is belt and braces.
// - Callers receive a deep copy, so a page that mutates its result cannot poison
//   what the next page reads.
// - A rejected fetch is evicted, so the next caller retries instead of inheriting
//   the error.
// - Eviction is automatic: installGroupDataEviction registers one axios response
//   interceptor that drops the matching keys whenever a successful non-GET
//   response targets one of the cached resources. Pages never call evict.

export const DEFAULT_TTL_MS = 5 * 60 * 1000

interface Entry {
  promise: Promise<any>
  expiresAt: number
  settled: boolean
}

const entries = new Map<string, Entry>()

export function currentGroupId(): string {
  try {
    const id = JSON.parse(localStorage.getItem('group') || '{}').id
    return id == null ? '' : String(id)
  } catch (e) {
    return ''
  }
}

function scopedKey(key: string): string {
  return `${currentGroupId()}|${key}`
}

function clone<T>(value: T): T {
  if (value === null || typeof value !== 'object') return value
  return JSON.parse(JSON.stringify(value))
}

/**
 * Return the cached value for key in the current group, share the in-flight request
 * if one is pending, or run fetcher and cache its result for ttlMs.
 */
export function getCached<T>(key: string, fetcher: () => Promise<T>, ttlMs: number = DEFAULT_TTL_MS): Promise<T> {
  const sk = scopedKey(key)
  const existing = entries.get(sk)
  if (existing && (!existing.settled || existing.expiresAt > Date.now())) {
    return existing.promise.then(clone)
  }
  const entry: Entry = { promise: Promise.resolve(undefined), expiresAt: 0, settled: false }
  entry.promise = Promise.resolve()
    .then(fetcher)
    .then(
      (value) => {
        entry.settled = true
        entry.expiresAt = Date.now() + ttlMs
        return value
      },
      (err) => {
        if (entries.get(sk) === entry) entries.delete(sk)
        throw err
      },
    )
  entries.set(sk, entry)
  return entry.promise.then(clone)
}

/** Drop entries, in every group, whose key equals `key` or starts with `key + ':'`. */
export function evict(key: string): void {
  for (const sk of Array.from(entries.keys())) {
    const bare = sk.slice(sk.indexOf('|') + 1)
    if (bare === key || bare.startsWith(key + ':')) entries.delete(sk)
  }
}

export function clearGroupDataCache(): void {
  entries.clear()
}

// A write to a URL matching a pattern evicts the listed keys. Patterns are tested
// against the full request URL. The site-settings entry also covers the
// resource-calendar variant through the prefix rule in evict().
const EVICTION_RULES: Array<{ pattern: RegExp; keys: string[] }> = [
  { pattern: /\/groups\/\d+\/games(\/|\?|$)/, keys: ['games'] },
  { pattern: /\/booking-slot-settings(\/|\?|$)/, keys: ['booking-slot-settings'] },
  { pattern: /\/users\/site-settings(\/|\?|$)/, keys: ['site-settings'] },
  { pattern: /\/workflow-states(\/|\?|$)/, keys: ['workflow-states'] },
  { pattern: /\/payment-(methods|settings)(\/|\?|$)/, keys: ['payment-methods'] },
  { pattern: /\/users\/filters(\/|\?|$)/, keys: ['saved-filters'] },
  { pattern: /\/announcements(\/|\?|$)/, keys: ['announcements-unread'] },
  { pattern: /\/users\/me(\/|\?|$)/, keys: ['users-me'] },
]

/** Exported for tests: the cache keys a write to this URL invalidates. */
export function keysEvictedByUrl(url: string): string[] {
  const out: string[] = []
  for (const rule of EVICTION_RULES) {
    if (rule.pattern.test(url)) {
      for (const k of rule.keys) if (!out.includes(k)) out.push(k)
    }
  }
  return out
}

const READ_METHODS = ['get', 'head', 'options']

/** Register the response interceptor that evicts on successful non-GET responses. */
export function installGroupDataEviction(axiosInstance: { interceptors: { response: { use: Function } } }): void {
  axiosInstance.interceptors.response.use(
    (res: any) => {
      const method = String((res && res.config && res.config.method) || 'get').toLowerCase()
      if (!READ_METHODS.includes(method)) {
        keysEvictedByUrl(String((res && res.config && res.config.url) || '')).forEach(evict)
      }
      return res
    },
    (err: any) => Promise.reject(err),
  )
}
